import { useEffect, useRef, useState } from "react";
import * as sopralluoghiApi from "../../api/sopralluoghi";
import { Sopralluogo } from "../../api/sopralluoghi";
import { Input } from "./Input";

/** Quel poco che serve a nominare un sopralluogo già collegato. */
export interface SopralluogoCollegato {
  id: string;
  insegna_cliente: string | null;
  data_sopralluogo: string;
  stato: string;
}

interface Props {
  /** I sopralluoghi già collegati, come arrivano dal record. */
  collegati: SopralluogoCollegato[];
  /** Gli id scelti adesso nel form — è questo che si salva. */
  value: string[];
  onChange: (ids: string[], collegati: SopralluogoCollegato[]) => void;
}

/**
 * Collega uno o più sopralluoghi a un'installazione.
 *
 * ## Perché più di uno
 *
 * Un punto vendita può essere stato rilevato più volte prima che
 * l'installazione partisse — un primo giro, poi un secondo dopo lavori. Fino al
 * 30/08/2026 il collegamento era una chiave singola e tutti tranne uno andavano
 * persi, in silenzio.
 *
 * ## Perché a chip e non a tendina
 *
 * Lo stesso motivo di `SedeAutocomplete` con `soloSelezione`: il testo digitato
 * qui non si salva da nessuna parte, serve solo a trovare. Mostrarlo come
 * campo modificabile inviterebbe a correggerlo, e ogni correzione non vorrebbe
 * dire niente. Quello che conta è l'insieme scelto, e si vede.
 *
 * ## «Non idoneo» si può collegare, e non si suggerisce
 *
 * Un sopralluogo che dichiara il posto inadatto resta legittimamente senza
 * installazione: non va proposto né trattato come lavoro arretrato. Ma se
 * qualcuno lo collega a mano — perché l'installazione si è fatta lo stesso, o
 * altrove — il campo non lo impedisce: non è un errore, è una storia che chi
 * era sul posto conosce meglio di questo form.
 */
export function SopralluoghiCollegati({ collegati, value, onChange }: Props) {
  const [testo, setTesto] = useState("");
  const [risultati, setRisultati] = useState<Sopralluogo[]>([]);
  const [open, setOpen] = useState(false);
  const [noti, setNoti] = useState<SopralluogoCollegato[]>(collegati);
  const containerRef = useRef<HTMLDivElement>(null);

  // I collegati arrivano dal record e si UNISCONO a quelli scelti adesso, non li
  // sostituiscono.
  //
  // Sostituirli era un difetto vero, trovato in uso il 31/08/2026: il padre
  // costruisce `collegati` con un `.map()` dentro il JSX, quindi e' un array
  // NUOVO a ogni render. L'effetto scattava a ogni giro e rimetteva `noti`
  // all'elenco del record — buttando via il sopralluogo appena aggiunto, un
  // istante dopo averlo aggiunto. A schermo il click sembrava non fare niente,
  // mentre il salvataggio funzionava benissimo: `value` era giusto, mancava
  // solo l'oggetto da cui prendere il nome per il chip.
  //
  // La fusione e' idempotente, quindi non importa quante volte scatti. Le voci
  // di un record precedente restano dentro senza danno: `scelti` qui sotto
  // filtra comunque su `value`, che e' la verita' su cosa e' collegato adesso.
  useEffect(() => {
    setNoti((precedenti) => {
      const per_id = new Map(precedenti.map((n) => [n.id, n]));
      for (const c of collegati) per_id.set(c.id, c);
      return [...per_id.values()];
    });
  }, [collegati]);

  useEffect(() => {
    const q = testo.trim();
    if (q.length < 2) {
      setRisultati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      sopralluoghiApi
        .listSopralluoghi({ q })
        .then(({ records }) => {
          if (!annullato) {
            setRisultati(records);
            setOpen(records.length > 0);
          }
        })
        .catch(() => {
          // Il collegamento è un extra: se la ricerca non risponde, il resto
          // del pannello resta utilizzabile.
          if (!annullato) setRisultati([]);
        });
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo]);

  useEffect(() => {
    function fuori(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", fuori);
    return () => document.removeEventListener("mousedown", fuori);
  }, []);

  function aggiungi(s: Sopralluogo) {
    if (value.includes(s.id)) return; // già collegato: niente doppioni
    const nuovo: SopralluogoCollegato = {
      id: s.id,
      insegna_cliente: s.insegna_cliente,
      data_sopralluogo: s.data_sopralluogo,
      stato: s.stato,
    };
    const aggiornati = [...noti.filter((n) => n.id !== s.id), nuovo];
    setNoti(aggiornati);
    onChange([...value, s.id], aggiornati);
    setTesto("");
    setOpen(false);
  }

  function togli(id: string) {
    const aggiornati = noti.filter((n) => n.id !== id);
    setNoti(aggiornati);
    onChange(
      value.filter((v) => v !== id),
      aggiornati
    );
  }

  // Solo quelli effettivamente scelti: `noti` può contenere anche roba tolta e
  // riaggiunta nella stessa sessione del pannello.
  const scelti = value.map((id) => noti.find((n) => n.id === id)).filter((x): x is SopralluogoCollegato => !!x);

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink">Sopralluoghi collegati</label>

      {scelti.length > 0 && (
        <ul className="flex flex-col gap-1">
          {scelti.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
            >
              <span className="text-sm text-ink">
                {s.insegna_cliente || "Sopralluogo"}
                <span className="text-ink/50">
                  {" "}
                  — {s.data_sopralluogo.slice(0, 10).split("-").reverse().join("/")} · {s.stato}
                </span>
              </span>
              <button
                type="button"
                onClick={() => togli(s.id)}
                aria-label={`Scollega ${s.insegna_cliente ?? "il sopralluogo"}`}
                className="shrink-0 rounded px-1.5 text-lg leading-none text-ink/40 hover:text-ink"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <Input
        label=""
        value={testo}
        placeholder="Cerca per insegna, ragione sociale o sede…"
        autoComplete="off"
        hint={scelti.length === 0 ? "Nessun sopralluogo collegato." : undefined}
        onChange={(e) => setTesto(e.target.value)}
        onFocus={() => risultati.length > 0 && setOpen(true)}
      />

      {open && risultati.length > 0 && (
        <ul className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-white shadow-card">
          {risultati.map((s) => {
            const gia = value.includes(s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  disabled={gia}
                  onClick={() => aggiungi(s)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-primary/5 disabled:opacity-40"
                >
                  <span className="font-medium text-ink">{s.insegna_cliente || "Sopralluogo senza insegna"}</span>
                  <span className="text-xs text-ink/50">
                    {s.data_sopralluogo.slice(0, 10).split("-").reverse().join("/")} · {s.stato}
                    {gia && " · già collegato"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
